/**
 * Browser AssetSource — bridges the virtual assetStore to the per-boot
 * `ServerAssetManager` (test-utils `initializer` asset seam). Decodes base64 ->
 * bytes, and images -> HTMLImageElement via a blob URL so game code can
 * ctx.drawImage() synchronously after preload.
 */
import type { AssetSource, AssetEntry, LoadedAsset } from '@gotti/core';
import { listAssets, getAsset, base64ToBytes } from './assetStore';
import { bytesToLoadedAsset } from './assetDecode';

/**
 * Strip a leading `client/` namespace segment.
 *
 * The per-boot `ServerAssetManager` keys its cache by FULL namespaced path and
 * always calls `source.load('<namespace>/<path>')` (e.g. `client/images/x.png`),
 * but the sandbox store is namespace-less (`images/x.png`). The whole sandbox
 * asset library IS the client namespace, so a leading `client/` is dropped
 * before the store lookup. A path that is already bare passes through unchanged,
 * so BOTH `client/images/x.png` and `images/x.png` resolve the same asset.
 * Mirrors `nodeGameBoot.assetSourceFromRefs`' namespace strip.
 */
export function stripClientNamespace(path: string): string {
  return path.startsWith('client/') ? path.slice('client/'.length) : path;
}

/**
 * The preload list handed to the `initializer` asset seam (`opts.assets.preload`):
 * every stored asset whose Preload box is checked (`preload !== false`; absent
 * counts as checked), in the FULL namespaced form the manager preloads with
 * (`loadRaw('client/<path>')`). Unchecked assets are omitted — they stay
 * loadable on demand but are not warmed before systems start.
 */
export function checkedAssetPreloadPaths(): string[] {
  return listAssets()
    .filter((a) => a.preload !== false)
    .map((a) => `client/${a.path}`);
}

export function createBrowserAssetSource(): AssetSource {
  return {
    manifest(): AssetEntry[] {
      return listAssets().map((a) => ({ path: a.path, mimeType: a.mimeType, size: a.size, hash: a.hash }));
    },
    async load(path: string): Promise<LoadedAsset> {
      const rel = stripClientNamespace(path);
      const a = getAsset(rel);
      if (!a) throw new Error(`browserAssetSource: no asset "${path}"`);
      // Keep the caller's (full namespaced) path on the LoadedAsset, mirroring
      // nodeGameBoot's source — the manager caches by the loadRaw key regardless.
      return bytesToLoadedAsset(path, a.mimeType, base64ToBytes(a.base64));
    },
  };
}
